import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode
} from 'react'
import { useAuthState } from '../auth/AuthProvider'
import { getSecretaryService } from '../services/Services'

export type Role = 'doctor' | 'secretary' | null

interface RoleContextProps {
  role: Role
  doctorId: string | null
  loading: boolean
}

const RoleContext = createContext<RoleContextProps>({
  role: null,
  doctorId: null,
  loading: true
})

export const RoleProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { uid, isAuthenticated, isLoading: authLoading } = useAuthState()
  const [role, setRole] = useState<Role>(null)
  const [doctorId, setDoctorId] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    if (authLoading) {
      return
    }

    if (!isAuthenticated || !uid) {
      setRole(null)
      setDoctorId(null)
      setLoading(false)
      return
    }

    setLoading(true)

    let cancelled = false

    const detectRole = async () => {
      try {
        const secretary = await getSecretaryService().getSecretaryByUid(uid)
        if (cancelled) return

        if (secretary) {
          if (secretary.status === 'invited') {
            await getSecretaryService().activateSecretary()
          }
          if (secretary.status === 'disabled') {
            throw new Error('Secretary access is disabled')
          }
          setRole('secretary')
          setDoctorId(secretary.doctorId)
        } else {
          setRole('doctor')
          setDoctorId(uid)
        }
      } catch {
        if (!cancelled) {
          setRole('doctor')
          setDoctorId(uid)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    detectRole()

    return () => {
      cancelled = true
    }
  }, [uid, isAuthenticated, authLoading])

  return (
    <RoleContext.Provider value={{ role, doctorId, loading }}>
      {children}
    </RoleContext.Provider>
  )
}

export const useRole = (): RoleContextProps => {
  return useContext(RoleContext)
}
